import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { Markdown } from './markdown'
import { ToolCallGroup } from './tool-call-group'
import { ToolCallCard } from './tool-call-card'
import { getToolRenderer } from './tool-registry'
import { useToolRendererRegistry } from '../../contexts/tool-renderer-registry'
import type { ChatMessage, ToolCall } from './types'
import type { ToolRendererOptions } from './tool-registry'

/**
 * Reveal streamed text at a steady, frame-aligned cadence so it reads smoothly
 * even though the network delivers it in irregular bursts. `shown` catches up
 * to `target` a fraction of the backlog per animation frame; when smoothing is
 * off (a completed / historical message) the full text renders immediately.
 */
function useSmoothText(target: string, enabled: boolean): string {
  const [shown, setShown] = useState(enabled ? '' : target)
  const targetRef = useRef(target)
  targetRef.current = target
  const shownRef = useRef(shown)
  shownRef.current = shown

  useEffect(() => {
    if (!enabled) {
      shownRef.current = targetRef.current
      setShown(targetRef.current)
      return
    }
    let raf = 0
    const tick = () => {
      const t = targetRef.current
      const cur = shownRef.current
      if (cur !== t) {
        // Non-append change (reset/replace) → snap; otherwise ease forward,
        // revealing ~1/6 of the backlog per frame (min 2 chars) so long bursts
        // catch up quickly while short ones still animate.
        const next = t.startsWith(cur)
          ? t.slice(
              0,
              cur.length + Math.max(2, Math.ceil((t.length - cur.length) / 6))
            )
          : t
        shownRef.current = next
        setShown(next)
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [enabled])

  return enabled ? shown : target
}

/** Assistant text with smooth streaming reveal (markdown-rendered). */
function AssistantText({
  text,
  streaming,
}: {
  text: string
  streaming: boolean
}) {
  const shown = useSmoothText(text, streaming)
  return <Markdown text={shown} />
}

interface MessageProps {
  message: ChatMessage
  rendererOptions?: ToolRendererOptions
  /** Display name shown under a user message. Default "You". */
  userName?: string
  /** Display name shown under an assistant message. Default "Assistant". */
  agentName?: string
  /** Resolved content rendered under THIS message (e.g. a "was this
   * helpful?" control) — the host computes it per-message via
   * `Transcript`'s `renderMessageFooter`, not a hardcoded floating
   * overlay tied to only the latest reply. */
  footer?: ReactNode
}

function AttachmentTile({
  url,
  filename,
  mimeType,
}: {
  url: string
  filename: string
  mimeType: string
}) {
  if (mimeType.startsWith('image/')) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="block max-w-xs overflow-hidden rounded-md border border-black/10 dark:border-white/10"
      >
        <img src={url} alt={filename} className="max-h-64 w-auto" />
      </a>
    )
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-xs items-center gap-1.5 rounded-md border border-black/10 px-2 py-1 text-xs hover:bg-foreground/5 dark:border-white/10"
    >
      <span className="min-w-0 truncate">{filename}</span>
    </a>
  )
}

export function Message({
  message,
  rendererOptions,
  userName = 'You',
  agentName = 'Assistant',
  footer,
}: MessageProps) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'
  // A named person on the assistant side of the transcript (a support
  // teammate, a second customer). Renders like a user turn: raw text, no
  // streaming skeleton, but left-aligned with their own name.
  const isHuman = !isUser && message.authorKind === 'human'
  // Context-supplied registry takes precedence over the module singleton —
  // mirrors ToolCallCard's own lookup so a tool registered either way is
  // found consistently.
  const toolRendererCtx = useToolRendererRegistry()
  const hasCustomRenderer = (name: string) =>
    Boolean(toolRendererCtx?.renderers[name] ?? getToolRenderer(name))

  if (isSystem) {
    return (
      <div className="my-2 flex justify-center">
        <p className="rounded-full bg-foreground/5 px-3 py-1 text-[0.7rem] text-muted-foreground">
          {message.parts
            .filter(
              (p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text'
            )
            .map((p) => p.text)
            .join(' ')}
        </p>
      </div>
    )
  }

  // Text-based transcript style: no bubbles. Each message leads with the
  // author's name, then a short divider, then the content (markdown for the
  // assistant, raw text for the user). User messages hug the RIGHT edge, the
  // assistant the left.
  const name = message.authorName ?? (isUser ? userName : agentName)

  // While the assistant is streaming but no text token has landed yet, show a
  // three-line skeleton where the reply will render (the composer/thinking
  // indicator covers the "still working" signal — no pulsing dot needed).
  const hasText = message.parts.some((p) => p.type === 'text' && p.text)
  const showSkeleton =
    !isUser && !isHuman && message.status === 'streaming' && !hasText

  // Render parts in order, but collapse each run of consecutive tool-call parts
  // into ONE grouped, expandable component (names only, current one shown while
  // streaming). Text/attachment parts render inline around the groups.
  const streaming = message.status === 'streaming'
  const rendered: ReactNode[] = []
  let toolRun: ToolCall[] = []
  const flushTools = (key: string) => {
    if (toolRun.length === 0) return
    rendered.push(
      <ToolCallGroup
        key={`tools-${key}`}
        toolCalls={toolRun}
        streaming={streaming}
      />
    )
    toolRun = []
  }
  message.parts.forEach((part, idx) => {
    const key = `${message.id}-${idx}`
    if (part.type === 'tool-call') {
      // A tool with a registered custom renderer (e.g. one that returns a
      // deep link, not just a name) breaks out of the anonymous "N tools"
      // group and renders its own card — everything else keeps batching
      // into ToolCallGroup exactly as before this branch existed.
      if (hasCustomRenderer(part.toolCall.name)) {
        flushTools(key)
        rendered.push(
          <ToolCallCard
            key={`tool-${key}`}
            toolCall={part.toolCall}
            rendererOptions={rendererOptions}
          />
        )
        return
      }
      toolRun.push(part.toolCall)
      return
    }
    flushTools(key)
    if (part.type === 'text') {
      if (!part.text) return
      rendered.push(
        <div
          key={key}
          className="min-w-0 max-w-[85%] break-words text-sm leading-relaxed text-foreground"
        >
          {/* Human-typed messages (the user, or a named person on the other
              side) stay raw: surprise formatting (e.g. # turning into a
              heading) would feel wrong. Agent messages render as markdown. */}
          {isUser || isHuman ? (
            <span className="whitespace-pre-wrap">{part.text}</span>
          ) : (
            <AssistantText text={part.text} streaming={streaming} />
          )}
        </div>
      )
    } else if (part.type === 'attachment') {
      rendered.push(
        <AttachmentTile
          key={key}
          url={part.attachment.url}
          filename={part.attachment.filename}
          mimeType={part.attachment.mimeType}
        />
      )
    }
  })
  flushTools('end')

  return (
    <div
      data-slot="agent-chat-message"
      data-role={message.role}
      data-author-kind={isUser ? 'user' : isHuman ? 'human' : 'agent'}
      className={cn(
        'group/message flex w-full min-w-0 flex-col gap-1.5 py-3',
        isUser ? 'items-end text-right' : 'items-start text-left'
      )}
    >
      <div className="text-xs font-medium text-muted-foreground">{name}</div>
      {/* Short rule between the name and the message body. */}
      <div aria-hidden className="h-px w-24 bg-black/10 dark:bg-white/10" />
      {rendered}
      {showSkeleton && (
        <div
          className="flex w-full max-w-[85%] flex-col gap-2"
          aria-label="Loading response"
        >
          <div className="h-3 w-[90%] animate-pulse rounded bg-foreground/10" />
          <div className="h-3 w-full animate-pulse rounded bg-foreground/10" />
          <div className="h-3 w-[60%] animate-pulse rounded bg-foreground/10" />
        </div>
      )}
      {footer}
    </div>
  )
}
